import { useCallback, useState } from "react"
import styled from "styled-components";
import { Columns, Row, Rows } from "../../Utils/Styling";
import { AnnotationGroupAnnotationTable, } from "./AnnotationGroupAnnotationTable";
import { useEffect } from "react";
import { checkbox } from "../../../../Components/DataTable/SelectHandler"
import { InfoMessage } from "../../../../Constants/StyledComponents";
import PrimaryCTAButton, { buttonColor } from "../../../../Components/PrimaryCTAButton/PrimaryCTAButton";
import { useRecoilState, useRecoilValue } from "recoil"
import { useAnnotationService } from "../../../../Hooks/useAnnotationService";
import { annotationGroupsAtom } from "../../../../Pages/Data/Visualize/DataReview/Atoms/Annotations";
import { activeAnnotationSessionAtom, selectedAnnotationGroupAtom } from "../../../../Pages/Data/Visualize/DataReview/Atoms/Annotations";
import { useDataReviewPermissions } from "../../../../Hooks/useDataReviewPermissions";
import { useModalProvider } from "../../../../Providers/ModalProvider";
import ConfirmationModal from "../../../../Components/ConfirmationModal/ConfirmationModal";
import { MobergTheme } from "../../../../Moberg";
import { useTableContext } from "../../../../Providers/TableProvider";

export function AnnotationGroupEditor() {
	const [selectedAnnotationGroup, setSelectedAnnotationGroup] = useRecoilState(selectedAnnotationGroupAtom)
	const annotationGroups = useRecoilValue(annotationGroupsAtom)
	const [activeAnnotationSession, setActiveAnnotationSession] = useRecoilState(activeAnnotationSessionAtom)
	const [sessionName, setSessionName] = useState("")
	const [canRename, setCanRename] = useState(false)
	const [selectedAnnotations, setSelectedAnnotations] = useState([])
	const { deleteAnnotations, deleteAnnotationGroups, renameAnnotationGroup } = useAnnotationService()
	const { createModal } = useModalProvider()
	const { setToastProps } = useTableContext()
const { canManageDataReview } = useDataReviewPermissions()
	const selectedGroupIsReadOnly = !!selectedAnnotationGroup?.read_only
	const canModifySelectedGroup = canManageDataReview && !selectedGroupIsReadOnly
	const writableAnnotationGroups = annotationGroups.filter(group => !group.read_only)

	const disableVisualStyle = (enabled, forInput = false) => 
		forInput 
			? { opacity: enabled ? 1 : 0.5, cursor: enabled ? 'text' : 'not-allowed' } 
			: { opacity: enabled ? 1 : 0.5, pointerEvents: enabled ? 'auto' : 'none' }

const handleRenameSession = () => {
		if (!canModifySelectedGroup) {
			return
		}

		// Returned so the confirmation waits for the result; the new name shows only after the rename succeeds.
		return renameAnnotationGroup(selectedAnnotationGroup, sessionName)
			.then(() => {
				setCanRename(false);
				setSelectedAnnotationGroup(prev => ({...prev, group_name: sessionName}));
				setActiveAnnotationSession(prev => ({...prev, group_name: sessionName}))}
				)
			.catch(() => {
				setToastProps({ isOpen: true, severity: "error", message: "Could not rename session. Please try again." })
			})
	}

	const renderRenameConfirmationModal = () => {
		if (!canModifySelectedGroup) {
			return
		}

		createModal(
			<ConfirmationModal
				title="Rename session?"
				description={`Are you sure you want to rename this session from "${selectedAnnotationGroup.group_name}" to "${sessionName}"?`}
				confirmButton={{
					text: "Yes, rename session",
					theme: MobergTheme.BLUE,
					onClick: () => handleRenameSession()
				}}
			/>
		)
	}

	const renderDeleteConfirmationModal = (type) => {
		if (!canModifySelectedGroup) {
			return
		}

		const config = type === 'annotations' 
			? {
				title: "Delete annotations",
				description: "Are you sure you want to permanently delete these annotations? This action cannot be undone.",
				buttonText: "Yes, delete annotations",
				onConfirm: () => handleDeleteAnnotations()
			  }
			: {
				title: "Delete session",
				description: "Are you sure you want to permanently delete this session? This action cannot be undone.",
				buttonText: "Yes, delete session",
				onConfirm: () => handleDeleteSession()
			  };
	
		createModal(
			<ConfirmationModal
				title={config.title}
				description={config.description}
				confirmButton={{
					text: config.buttonText,
					theme: MobergTheme.RED,
					onClick: config.onConfirm
				}}
			/>
		)
	}

	const handleDeleteSession = () => {
		if (!canModifySelectedGroup) {
			return
		}

		const deletedGroup = selectedAnnotationGroup
		const restSessions = annotationGroups.filter(g => g.id !== deletedGroup.id && !g.read_only)

		// Returned so the confirmation waits for the result; the session is switched only after the delete succeeds.
		return deleteAnnotationGroups([deletedGroup])
			.then(() => {
				if (activeAnnotationSession && deletedGroup.id === activeAnnotationSession.id) {
					setActiveAnnotationSession(restSessions[0])
				}

				const sessionSelectBox = checkbox(`row-${deletedGroup.id}`)
				setSelectedAnnotationGroup(null)
				if (sessionSelectBox?.checked) {
					sessionSelectBox.click()
				} // release the selection if the session is selected
				// setState is asynchronous, so the length of selectedAnnotationGroups would not immediately got -1
			})
			.catch(() => {
				setToastProps({ isOpen: true, severity: "error", message: "Could not delete session. Please try again." })
			})
	}

	const handleDeleteAnnotations = () => {
		if (!canModifySelectedGroup) {
			return
		}

		return deleteAnnotations(selectedAnnotations.map(annotation => annotation.id))
			.catch(() => {
				setToastProps({ isOpen: true, severity: "error", message: "Could not delete annotations. Please try again." })
			})
	}

	const handleCancel = e => {
		setSelectedAnnotationGroup(null)
	}

	useEffect(() => {
		setSessionName(selectedAnnotationGroup ? selectedAnnotationGroup.group_name : "")
		setCanRename(false)
	}, [selectedAnnotationGroup, setSessionName])

	const handleNameInput = useCallback(e => {
		if (e.target.value === selectedAnnotationGroup.group_name) {
			setCanRename(false)
		} else {
			setCanRename(true)
		}
		setSessionName(e.target.value)
	}, [selectedAnnotationGroup])

	return selectedAnnotationGroup ?
		(<AnnotationEditDiv spacing="5px" style={{ height: "fit-content", minHeight: "98%", overflowX: "unset", border: "0.5px solid var(--color-border-default)", borderRadius: "6px", margin: "7px 0", padding: "5px" }}>
			<Rows gap="10px">
				<Columns>
					<h1 style={{ margin: "3px 7px -3px 36px" }}>Session:</h1>
					<input type="text" onChange={handleNameInput} value={sessionName}
						disabled={!canModifySelectedGroup}
						style={{
							border: "solid 1px var(--color-border-default)", margin: "0 5px 0 9px", height: "30px",
							padding: "0 6px", borderRadius: "5px", width: "100%", maxWidth: "206px", minWidth: 0, outline: "var(--color-accent-interactive)",
							backgroundColor: "var(--color-input-bg)", color: "var(--color-text-primary)",
							...disableVisualStyle(canModifySelectedGroup, true)
						}}
					/>
					<PrimaryCTAButton
						buttonText="Rename"
						buttonStyle={{
							height: "30px",
							width: "70px",
							fontSize: "14px",
							flexShrink: 0,
							...disableVisualStyle(canModifySelectedGroup)
						}}
						disabled={!canRename || !canModifySelectedGroup}
						color={buttonColor.blueFill}
						onClickFxn={renderRenameConfirmationModal}
					/>
				</Columns>
				<Row>
					{/* <h1>Annotations: </h1> */}
					<AnnotationGroupAnnotationTable tableHeight="200px" handleSetSelectedAnnotations={setSelectedAnnotations} />
				</Row>
			</Rows>


			<Columns justifyContent="space-between" style={{ marginTop: "20px" }}>
				<Columns gap="10px" justifyContent="flex-end">
					<PrimaryCTAButton
						buttonText="Cancel"
						onClickFxn={handleCancel}
						color={buttonColor.redOutline}
						buttonStyle={{ height: "34px", padding: "0px 10px 0px 10px", marginRight: "0px" }}
					/>
					<PrimaryCTAButton
						buttonText="Delete annotations"
						onClickFxn={() => renderDeleteConfirmationModal('annotations')}
						disabled={!(selectedAnnotations.length > 0) || !canModifySelectedGroup}
						color={buttonColor.redFill}
						buttonStyle={{ 
							height: "34px", 
							padding: "0px 10px 0px 10px", 
							marginRight: "0px", 
							...disableVisualStyle(canModifySelectedGroup) 
						}}
					/>

					<PrimaryCTAButton
						buttonText="Delete session"
						onClickFxn={() => renderDeleteConfirmationModal('session')}
						disabled={writableAnnotationGroups.length <= 1 || !canModifySelectedGroup}
						color={buttonColor.redFill}
						buttonStyle={{ 
							height: "34px", 
							padding: "0px 10px 0px 10px", 
							marginRight: "0px", 
							...disableVisualStyle(canModifySelectedGroup) 
						}}
					/>
				</Columns>
			</Columns>

		</AnnotationEditDiv>)
		:
		(<InfoMessage style={{ height: "auto", width: "400px", padding: "6px", margin: "40px" }}>
			<p><strong>Info</strong>: Please select a session to view annotations.</p>
		</InfoMessage>)
}

const AnnotationEditDiv = styled(Rows)`
	h2 {
        font-family: 'Source Sans Pro';
        font-style: normal;
        font-weight: 700;
        font-size: 21px;
        line-height: 150%;
        color: var(--color-text-primary);
        margin-top: 5px;
		text-align: center;
    }
	h1 {
        font-family: 'Source Sans Pro';
        font-style: normal;
        font-weight: 600;
        font-size: 16px;
        line-height: 150%;
        color: var(--color-text-primary);
    }
`;
